import PropTypes from 'prop-types' // eslint-disable-line no-unused-vars
import { PureComponent } from 'react'
import { uniq, values, invert, flatten, intersection, isEqual } from 'lodash'
import TextInput from '../../inputs/text'
import { deleteColor, dividerColor, giphyIndigo, giphyGreen, giphyGrey, giphyBlack } from 'shared/css/colors.css'
import css from './tags.css'
import styled, { css as styledCss } from 'styled-components'
import EditPanelContext from '../context'
import { message } from 'shared/redux/message'
import { STATUS_STYLE } from 'shared/components/message/flash-message'
import { connect } from 'react-redux'
import { TAG_LIMIT } from 'shared/variables'
import { SelectList } from '@giphy/react-giphy-brand'
import { fetchLanguageTags, addLanguageTags, removeLanguageTag } from 'shared/api/index'

const getLanguageTags = async (gifIds, tagLanguage) => {
    const isMultipleGifs = gifIds.length > 1
    const tagsData = await fetchLanguageTags(gifIds, tagLanguage)

    if (tagsData.gifs) {
        const gifTags = tagsData.gifs.map(g => g.tags.map(t => t.text))
        const allTags = uniq(flatten(gifTags))
        const commonTags = gifTags.length !== gifIds.length ? [] : intersection(...gifTags)

        const r = allTags.map(tag => ({
            tag,
            isCommon: isMultipleGifs && commonTags.indexOf(tag) > -1,
        }))
        return r
    }

    return []
}

const TagLanguages = {
    EN: '🇬🇧 English',
    KO: '🇰🇷 Korean',
    PT: '🇵🇹 Portuguese',
    RU: '🇷🇺 Russian',
    ES: '🇪🇸 Spanish',

    // AR: '🇸🇦 Arabic',
    // BN: '🇮🇳 Bengali',
    // CS: '🇨🇿 Czech',
    // DA: '🇩🇰 Danish',
    // DE: '🇩🇪 German',
    // FA: '🇮🇷 Persian',
    // FI: '🇫🇮 Finnish',
    // FR: '🇫🇷 French',
    // HI: '🇮🇳 Hindi',
    // HU: '🇭🇺 Hungarian',
    // ID: '🇮🇩 Indonesian',
    // IT: '🇮🇹 Italian',
    // IW: '🇮🇱 Hebrew',
    JA: '🇯🇵 Japanese',
    // MS: '🇲🇾 Malay',
    // NL: '🇳🇱 Dutch',
    // NO: '🇳🇴 Norwegian',
    // PL: '🇵🇱 Polish',
    // RO: '🇷🇴 Romanian',
    // SV: '🇸🇪 Swedish',
    // TH: '🇹🇭 Thai',
    // TR: '🇹🇷 Turkish',
    // UK: '🇺🇦 Ukranian',
    // VI: '🇻🇳 Vietnamese',
    // ZH_CN: '🇨🇳 Chinese (Simplified)',
    // ZH_TW: '🇹🇼 Chinese (Traditional)',
}

const Tag = styled.div`
    background: ${dividerColor};
    color: ${giphyGrey};
    border: ${props => (props.isCommon ? `1px solid ${giphyIndigo}` : 'none')};
    border-radius: 18px;
    display: ${props => (props.bulkEditMode ? 'block' : 'inline-block')};
    font-style: italic;
    height: 36px;
    line-height: 36px;
    margin: ${props => (props.bulkEditMode ? '0 0 6px 0;' : '0 8px 6px 0')};
    text-transform: none;
    user-select: none;

    ${props =>
        (props.isCommon || !props.isMultipleGifs) &&
        props.permissions.feature_tags &&
        props.isTagFeaturable &&
        styledCss`
        background: ${props.isFeatured ? giphyGreen : dividerColor};
        color: ${props.isFeatured ? giphyBlack : giphyGrey};
        cursor: pointer;

        &:hover {
            background: ${props.isFeatured ? dividerColor : giphyGreen};
            color: ${props.isFeatured ? giphyGrey : giphyBlack};
        }
    `};
`

const TagsContainer = styled.div`
    overflow-y: auto;

    ${props =>
        !props.bulkEditMode &&
        styledCss`
        max-height: 139px;
    `};
`

const LanguageContainer = styled.div`
    display: inline-block;
    height: 30px;

    ${props =>
        props.bulkEditMode
            ? styledCss`
        width: 100%;
        margin-top: 10px;
        `
            : styledCss`
        margin-top: 4px;
        margin-left: 8px;
        `};
`

const TagsLabel = styled.div`
    float: left;
    height: 30px;
    line-height: 30px;
`

const SelectLanguage = styled.div`
    float: right;
`

/**
 * Edit Panel - Tags: Controls the tag input
 * and pills from the GIF Edit Panel. This
 * allows user to add tags as well as remove
 * any pre-existing tags.
 *
 * @class EditPanelTags
 */
class EditPanelTags extends PureComponent {
    static contextType = EditPanelContext
    static propTypes = {
        className: PropTypes.string,
        user: PropTypes.object,
        permissions: PropTypes.object,
        gifs: PropTypes.array,
        tags: PropTypes.array,
        tagCounts: PropTypes.object,
        onAddTags: PropTypes.func.isRequired,
        onRemoveTag: PropTypes.func.isRequired,
        onRemoveAllTags: PropTypes.func,
        onFeatureTag: PropTypes.func,
        onUnfeatureTag: PropTypes.func,
    }
    static defaultProps = {
        user: {},
        permissions: {},
        gifs: [],
        tags: [],
        tagCounts: {},
        placeholder: 'Add a tag',
        onFeatureTag: () => {},
        onUnfeatureTag: () => {},
    }

    state = {
        tagLanguage: 'EN',
        tags: [],
    }

    componentDidMount() {
        this.updateTags()
    }

    componentDidUpdate(prevProps) {
        if (!isEqual(this.props.gifs, prevProps.gifs)) {
            this.updateTags()
        }
    }

    async updateTags() {
        const { tagLanguage } = this.state
        const { gifs, tags: englishTags } = this.props

        let tags = []

        if (tagLanguage === 'EN') {
            tags = englishTags
        } else {
            const gifIds = gifs.map(g => g.id)

            try {
                tags = await getLanguageTags(gifIds, tagLanguage)
            } catch (error) {
                message(`There was a problem fetching ${tagLanguage} tags.`, STATUS_STYLE.ERROR)
            }
        }

        this.setState({ tags: tags })
    }

    onRemove = tag => {
        const { gifs, onRemoveTag } = this.props
        const { tagLanguage } = this.state

        if (tagLanguage === 'EN') {
            onRemoveTag(tag)
        } else {
            const gifIds = gifs.map(g => g.id)

            for (const id of gifIds) {
                removeLanguageTag(id, tag, tagLanguage).then(() => this.updateTags())
            }
        }
    }

    onAdd = newTag => {
        const { tagLanguage, tags } = this.state
        const { gifs, onAddTags, message, user } = this.props

        const tagExists = tags.find(t => t.tag === newTag && t.isCommon)

        if (tagExists) {
            message('That tag already exists!', STATUS_STYLE.ERROR)
            return
        }

        let newTags = newTag.split(',')
        newTags = uniq(newTags.map(tag => tag.replace(/#/g, '').trim()))

        if (newTags.length + tags.length > TAG_LIMIT && !user.isStaff) {
            message('Sorry! Max number of tags reached.', STATUS_STYLE.ERROR)
            return
        }

        if (tagLanguage === 'EN') {
            onAddTags(newTags)
        } else {
            const gifIds = gifs.map(g => g.id)
            for (const id of gifIds) {
                addLanguageTags(id, newTags, tagLanguage).then(() => this.updateTags())
            }
        }
    }

    onTagLanguageChange(lang) {
        this.setState({ tagLanguage: lang || 'EN' }, this.updateTags)
    }

    render() {
        const {
            className,
            user,
            permissions,
            tagCounts,
            onFeatureTag,
            onUnfeatureTag,
            name,
            fixedWidth,
            placeholder,
            isMultipleGifs,
        } = this.props
        const { bulkEditMode } = this.context
        const { tags, tagLanguage } = this.state
        const isTagFeaturable = tagLanguage === 'EN'

        return (
            <div className={className}>
                {name && <span className={css.name}>{name}</span>}
                <div className={css.header}>
                    <TextInput
                        className={!fixedWidth ? css.tagInputFluid : css.tagInput}
                        type="text"
                        autoSelect={true}
                        placeholder={placeholder}
                        buttonLabel={<i className={css.inputAdd} />}
                        onSubmit={this.onAdd}
                    />
                    {isMultipleGifs && (
                        <TextInput
                            className={!fixedWidth ? css.tagInputFluid : css.tagInput}
                            type="text"
                            autoSelect={true}
                            placeholder={'Remove a tag'}
                            buttonLabel={<i className={css.inputRemove} />}
                            buttonColor={deleteColor}
                            onSubmit={this.onRemove}
                        />
                    )}

                    {permissions.edit_language_tags && (
                        <LanguageContainer bulkEditMode={bulkEditMode}>
                            {bulkEditMode && <TagsLabel>Tags </TagsLabel>}
                            <SelectLanguage>
                                <SelectList
                                    options={values(TagLanguages)}
                                    placeholder={'Language'}
                                    active={[TagLanguages[tagLanguage]]}
                                    theme={'transparent'}
                                    allowMultiple={false}
                                    onChange={value => this.onTagLanguageChange(invert(TagLanguages)[value])}
                                    extendUp={true}
                                />
                            </SelectLanguage>
                        </LanguageContainer>
                    )}
                </div>
                <TagsContainer bulkEditMode={bulkEditMode}>
                    {tags &&
                        tags.map(({ tag, isCommon, isFeatured }) => (
                            <Tag
                                key={tag.tag || tag}
                                user={user}
                                isCommon={isCommon}
                                isFeatured={isFeatured}
                                permissions={permissions}
                                isMultipleGifs={isMultipleGifs}
                                isTagFeaturable={isTagFeaturable}
                                onClick={() => {
                                    if (isTagFeaturable && permissions.feature_tags) {
                                        if (isMultipleGifs && !isCommon) {
                                            return
                                        }
                                        isFeatured ? onUnfeatureTag(tag) : onFeatureTag(tag)
                                    }
                                }}
                                bulkEditMode={bulkEditMode}
                            >
                                <span className={css.label}>
                                    {tag.tag || tag}{' '}
                                    {tagCounts[tag] && (
                                        <span className={css.tagCount}>{tagCounts[tag].toLocaleString()}</span>
                                    )}
                                </span>
                                <i
                                    className={css.remove}
                                    onClick={e => {
                                        e.stopPropagation()
                                        this.onRemove(tag)
                                    }}
                                />
                            </Tag>
                        ))}
                </TagsContainer>
            </div>
        )
    }
}

export default connect(undefined, { message })(EditPanelTags)
